import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import { store } from 'store';

import {
  apiAuthenticateHandoff,
  apiAuthenticateSession,
  apiLogin,
  apiLogout,
  apiSession,
  apiSessionReauth,
  isApiUrl,
} from '~constants/api';
import {
  ABSOLUTE_EXPIRES_IN_HEADER,
  BACKGROUND_HEADER,
  EXPIRES_IN_HEADER,
  EXPIRY_BROADCAST_INTERVAL_MS,
  FOREGROUND_BROADCAST_INTERVAL_MS,
  KEY_REREAD_DELAY_MS,
  PROOF_HEADER,
  SERVER_TIME_HEADER,
} from '~constants/session';

import {
  installActivityTracking,
  isUserActive,
  markForegroundRequest,
  noteForegroundRequestFromPeer,
} from '~libs/sessionActivity';
import { expireSession, requirePasswordConfirmation, resolveExpiredSession } from '~libs/sessionExpiry';
import {
  acquireKeyLock,
  commitPendingKey,
  createPendingKey,
  discardPendingKey,
  hasKeyLocks,
  resetKeyCache,
  waitForKeyCommit,
} from '~libs/sessionKey';
import { signProof, updateServerOffset } from '~libs/sessionProof';
import { rejectReauthWaiters, resolveReauthWaiters, waitForReauth } from '~libs/sessionReauth';
import { publishSessionMessage, subscribeSessionMessages } from '~libs/sessionSync';

import {
  applySessionTiming,
  removeAuthenticated,
  setForceRedirect,
  setReauthPrompt,
  setSessionExpiry,
} from '~main/reducers/login';

declare module 'axios' {
  interface AxiosRequestConfig {
    _proofRetried?: boolean;
    _reauthRetried?: boolean;
    _sessionRetried?: boolean;
    _keyRereadRetried?: boolean;
    _releaseKeyLock?: () => void;
    _pendingKeyId?: string;
  }
}

const pathOf = (target: string) => new URL(target, window.location.href).pathname;

// Endpoints that create a session. They carry a fresh public key in the body instead of a proof, because no
// key is registered yet.
const isEstablishPath = (path: string) =>
  [apiLogin, apiAuthenticateSession, apiAuthenticateHandoff].some((endpoint) => pathOf(endpoint) === path);

// Endpoints whose job is to establish a session from a cross-application credential.
// A 401 from one of these means the credential just presented was no good - a spent
// single-use handoff ticket, say - NOT that the caller's existing session has expired.
// Clearing the session here would sign a user out for replaying a stale link, which is
// an ordinary thing to do (pressing Back through a handoff lands on one). Leave the
// session intact and let the caller decide what to do.
//
// The session probes join them: their callers read the 401 themselves (bootstrap, the expiry check, the
// password modal, sign-out), and clearing the session underneath them would race those decisions.
const NO_SIGN_OUT_PATHS = () =>
  [apiAuthenticateHandoff, apiAuthenticateSession, apiSession, apiSessionReauth, apiLogout].map(pathOf);

const isExcludedFromSignOut = (fullUrl: string) => NO_SIGN_OUT_PATHS().includes(pathOf(fullUrl));

const releaseKeyLock = (config?: AxiosRequestConfig) => {
  const release = config?._releaseKeyLock;
  if (!release) return;
  config._releaseKeyLock = undefined;
  release();
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const fullUrlOf = (config?: AxiosRequestConfig): string | null => {
  if (!config) return null;
  try {
    return axios.getUri(config);
  } catch {
    return null;
  }
};

const readHeader = (headers: unknown, name: string): string | undefined => {
  if (!headers) return undefined;
  const source = headers as { get?: (key: string) => unknown } & Record<string, unknown>;
  const value = typeof source.get === 'function' ? source.get(name) : source[name];
  if (value === undefined || value === null) return undefined;
  return String(value);
};

const isBackgroundRequest = (config?: AxiosRequestConfig) => Boolean(readHeader(config?.headers, BACKGROUND_HEADER));

let lastExpiryBroadcastAt = 0;
let lastForegroundBroadcastAt = 0;

const broadcastExpiry = () => {
  const now = Date.now();
  if (now - lastExpiryBroadcastAt < EXPIRY_BROADCAST_INTERVAL_MS) return;
  const { session } = store.getState().main.login;
  if (!session) return;
  lastExpiryBroadcastAt = now;
  publishSessionMessage({
    type: 'expiry',
    expiresAt: session.expiresAt,
    absoluteExpiresAt: session.absoluteExpiresAt,
    reauthExpiresAt: session.reauthExpiresAt,
  });
};

const broadcastForeground = (at: number) => {
  if (at - lastForegroundBroadcastAt < FOREGROUND_BROADCAST_INTERVAL_MS) return;
  lastForegroundBroadcastAt = at;
  publishSessionMessage({ type: 'foreground', at });
};

// Every tab shares one cookie jar and one key, so a change in one is a change in all.
const subscribeToOtherTabs = () =>
  subscribeSessionMessages((message) => {
    switch (message.type) {
      case 'key-changed':
        resetKeyCache();
        break;
      case 'foreground':
        noteForegroundRequestFromPeer(message.at);
        break;
      case 'expiry':
        store.dispatch(applySessionTiming(message));
        break;
      case 'reauth-required':
        if (store.getState().main.login.authenticated) store.dispatch(setReauthPrompt('required'));
        break;
      case 'reauthed': {
        const { expiresAt, absoluteExpiresAt, reauthExpiresAt } = message;
        store.dispatch(setReauthPrompt('none'));
        store.dispatch(applySessionTiming({ expiresAt, absoluteExpiresAt, reauthExpiresAt }));
        resolveReauthWaiters();
        break;
      }
      case 'signed-out':
        rejectReauthWaiters('signed-out');
        store.dispatch(removeAuthenticated(message.reason));
        break;
      default:
        break;
    }
  });

/**
 * The API's `reason` from an error body. Downloads (`responseType: 'blob'`) receive the JSON body as a Blob, so
 * it is parsed first; without that every recovery branch is skipped. A Blob can be read
 * again, so callers that parse it later (buildBlobErrorResponse) still work.
 */
const reasonOf = async (data: unknown): Promise<string | undefined> => {
  let body = data;
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) body = JSON.parse(await data.text());
  } catch {
    return undefined;
  }
  const reason = (body as { reason?: unknown } | null | undefined)?.reason;
  return typeof reason === 'string' ? reason : undefined;
};

/**
 * Handles a 401 from an API URL. Returns a retried response, or rethrows.
 * Order: company-disabled, proof-invalid (one retry), reauth-required (hold and retry, never for excluded paths),
 * excluded paths,
 * then everything else signs the user out.
 */
const handleUnauthorised = async (error: AxiosError<unknown>) => {
  const { config, response } = error;
  const reason = await reasonOf(response?.data);
  const fullUrl = fullUrlOf(config) ?? '';
  const excluded = isExcludedFromSignOut(fullUrl);

  if (reason === 'company-disabled') store.dispatch(setForceRedirect());

  if (reason === 'proof-invalid' && config) {
    if (!config._proofRetried) {
      // Most often a clock that has drifted, or a key another tab has just replaced. The offset was updated
      // from this very response, and the key is re-read from storage, so one retry usually clears it. Waiting
      // on the key lock first means a sign-in committing in another tab is seen, not raced.
      await waitForKeyCommit();
      resetKeyCache();
      return axios.request({ ...config, _proofRetried: true });
    }
    if (!hasKeyLocks() && !config._keyRereadRetried) {
      // No Web Locks to wait on: give a commit in another tab a moment to land, then read the key once more.
      await wait(KEY_REREAD_DELAY_MS);
      resetKeyCache();
      return axios.request({ ...config, _keyRereadRetried: true });
    }
    if (!excluded) expireSession(store.dispatch, 'unverified');
    return Promise.reject(error);
  }

  if (reason === 'reauth-required' && config) {
    // The session probes, the reauth call itself and sign-out read their own 401s. Holding one of them would
    // wait on a modal that may not be mounted (Logout sits outside PrivateRoute), so it could never finish.
    if (excluded) return Promise.reject(error);
    requirePasswordConfirmation(store.dispatch);
    // Background traffic must never open a wait of its own: it rejects, and the caller decides.
    if (isBackgroundRequest(config) || config._reauthRetried) return Promise.reject(error);
    // Foreground work is held while the modal is open, then replayed with the fresh cookie and a fresh proof.
    // A rejection here means the user signed out or the reauth deadline passed.
    try {
      await waitForReauth();
    } catch {
      return Promise.reject(error);
    }
    return axios.request({ ...config, _reauthRetried: true });
  }

  if (excluded) return Promise.reject(error);

  if (pathOf(fullUrl) === pathOf(apiLogin)) {
    // A failed login is not an expiry: no reason is recorded, so the login page does not claim the session
    // expired, and other tabs (which may hold a good session) are not told anything.
    store.dispatch(removeAuthenticated());
    return Promise.reject(error);
  }

  if (reason === 'session-expired' && config && !config._sessionRetried) {
    // The request may have been sent with a cookie another tab has since rotated (login, reauth), in which
    // case the browser already holds a good session. Ask the API before signing every tab out. When it has
    // ended, resolveExpiredSession has signed out and broadcast already; when the API could not be asked,
    // this request fails on its own and the session is left alone.
    if ((await resolveExpiredSession(store.dispatch)) === 'active') {
      return axios.request({ ...config, _sessionRetried: true });
    }
    return Promise.reject(error);
  }

  expireSession(store.dispatch, 'expired');
  return Promise.reject(error);
};

export const setupAxiosInterceptors = () => {
  installActivityTracking();
  subscribeToOtherTabs();

  axios.interceptors.request.use(
    async (request) => {
      request.headers['UTC-Offset'] = dayjs().format('ZZ');

      const fullUrl = axios.getUri(request);
      // Credentials, the background rule and the proof are for the API alone, never a third-party host.
      if (!isApiUrl(fullUrl)) return request;

      request.withCredentials = true;

      // Automatic traffic (polling, SSE-triggered refetches, focus refetches) must not keep a session alive,
      // so anything not preceded by recent user input is marked background, unless the caller said so already.
      if (!readHeader(request.headers, BACKGROUND_HEADER)) {
        if (isUserActive()) {
          const now = Date.now();
          markForegroundRequest(now);
          broadcastForeground(now);
        } else {
          request.headers.set(BACKGROUND_HEADER, '1');
        }
      }

      const path = pathOf(fullUrl);
      if (isEstablishPath(path)) {
        // Held until the response commits or discards the key, so another tab cannot interleave.
        const release = await acquireKeyLock();
        try {
          const { pendingId, publicJwk } = await createPendingKey();
          request.data = { ...(request.data ?? {}), proof_key: publicJwk };
          request._pendingKeyId = pendingId;
          request._releaseKeyLock = release;
        } catch (error) {
          release();
          throw error;
        }
        return request;
      }

      // Only the method and path are signed, never the body, so FormData, uploads and blob downloads are
      // untouched. The query string is left out to match the API, which checks `htu` against the path only.
      const proof = await signProof(request.method ?? 'get', fullUrl);
      if (proof) request.headers.set(PROOF_HEADER, proof);
      else request.headers.delete(PROOF_HEADER);

      return request;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    async (response) => {
      updateServerOffset(response.headers?.[SERVER_TIME_HEADER]);

      if (response.config?._pendingKeyId) {
        const pendingId = response.config._pendingKeyId;
        try {
          await commitPendingKey(pendingId);
        } catch (commitError) {
          // The API has already issued a cookie session bound to a key this browser could not keep. Throw the
          // key away and revoke that session (best effort), so no unusable session is left live on the server.
          await discardPendingKey(pendingId);
          await axios.post(apiLogout, {}).catch(() => undefined);
          throw commitError;
        } finally {
          releaseKeyLock(response.config);
        }
      }

      const expiresIn = Number(response.headers?.[EXPIRES_IN_HEADER]);
      const absoluteExpiresIn = Number(response.headers?.[ABSOLUTE_EXPIRES_IN_HEADER]);
      if (
        response.headers?.[EXPIRES_IN_HEADER] !== undefined &&
        response.headers?.[ABSOLUTE_EXPIRES_IN_HEADER] !== undefined &&
        Number.isFinite(expiresIn) &&
        Number.isFinite(absoluteExpiresIn)
      ) {
        store.dispatch(setSessionExpiry({ expiresIn, absoluteExpiresIn }));
        broadcastExpiry();
      }

      return response;
    },
    async (error) => {
      updateServerOffset(error?.response?.headers?.[SERVER_TIME_HEADER]);

      if (error?.config?._pendingKeyId) {
        await discardPendingKey(error.config._pendingKeyId);
      }
      releaseKeyLock(error?.config);

      if (error?.response?.status === 401) return handleUnauthorised(error);

      return Promise.reject(error);
    },
  );
};
